// Phase 7 D3 — Track C T-C3. TimezonePicker.
//
// Q-14: timezone list comes from `Intl.supportedValuesOf('timeZone')` when
// available (modern engines: V8 ≥10, JSC ≥15.4, SpiderMonkey ≥125). When
// the runtime is older (or when Intl is partially polyfilled), we fall
// back to a small curated subset covering the major IANA regions.
//
// The picker is built on the existing `<Combobox/>` primitive (Phase 6
// dispatch added it; Q-14 references it). Selection writes via
// `PATCH /api/users/me/timezone`. Q-2 pessimistic UI: combobox is
// disabled while the round-trip is in flight; an inline status message
// announces success/error to assistive tech.

import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import type {
  UpdateTimezoneBody,
  UpdateTimezoneResponse,
} from '@dailydeck/shared/types';
import { copy } from '@dailydeck/shared/copy';
import { apiClient } from '../../../app/apiClient.js';
import { Combobox, type ComboboxItem } from '../../../components/ui/Combobox.js';

const FALLBACK_TIMEZONES: readonly string[] = [
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney',
  'Pacific/Auckland',
];

function readTimezones(): { items: readonly string[]; usedFallback: boolean } {
  // `Intl.supportedValuesOf` was added in ES2022; some bundlers / older
  // environments do not expose it on the typed Intl object. Fall back if
  // the function is missing.
  type IntlWithSupported = typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };
  const intl = Intl as IntlWithSupported;
  if (typeof intl.supportedValuesOf === 'function') {
    try {
      const list = intl.supportedValuesOf('timeZone');
      if (Array.isArray(list) && list.length > 0) {
        return { items: list, usedFallback: false };
      }
    } catch {
      // fall through to fallback
    }
  }
  return { items: FALLBACK_TIMEZONES, usedFallback: true };
}

function detectBrowserTimezone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === 'string' && tz.length > 0 ? tz : null;
  } catch {
    return null;
  }
}

interface TimezonePickerProps {
  /** Current value from the server (`platform_users.timezone`). May be null
   *  if the user has not set one yet — falls back to the browser's value. */
  value: string | null;
  onValueChange?: (next: string) => void;
}

export function TimezonePicker({
  value,
  onValueChange,
}: TimezonePickerProps): JSX.Element {
  const { items, usedFallback } = React.useMemo(() => readTimezones(), []);
  const browserTz = React.useMemo(() => detectBrowserTimezone(), []);
  const [selected, setSelected] = React.useState<string | null>(
    value ?? browserTz ?? items[0] ?? null,
  );
  const [statusMessage, setStatusMessage] = React.useState<string | null>(null);

  const comboItems: readonly ComboboxItem[] = React.useMemo(
    () => items.map((id) => ({ value: id, label: id })),
    [items],
  );

  const mutation = useMutation<UpdateTimezoneResponse, Error, UpdateTimezoneBody>({
    mutationFn: (body) => apiClient.usersMe.updateTimezone(body),
  });

  async function handleChange(next: string | null): Promise<void> {
    setStatusMessage(null);
    if (next === null || next === selected) return;
    setSelected(next);
    try {
      await mutation.mutateAsync({ timezone: next });
      onValueChange?.(next);
    } catch {
      setStatusMessage(copy.settings.theme.saveError);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor="settings-timezone-input"
        className="text-body-strong text-text-primary"
      >
        {copy.settings.theme.timezoneLabel}
      </label>
      <Combobox
        id="settings-timezone-input"
        value={selected}
        onValueChange={(next) => {
          void handleChange(next);
        }}
        items={comboItems}
        placeholder={copy.settings.theme.timezonePlaceholder}
        emptyMessage={copy.settings.theme.timezoneEmpty}
        disabled={mutation.isPending}
      />
      {usedFallback && (
        <p className="text-caption text-text-muted">
          {copy.settings.theme.timezoneNoticeFallback}
        </p>
      )}
      <p
        role="status"
        aria-live="polite"
        className="text-caption text-state-danger"
      >
        {statusMessage ?? ''}
      </p>
    </div>
  );
}
